/**
 * Session state for the Staff App.
 *
 * `/auth/me` is the ONLY source of identity, roles and permissions. The
 * permission array it returns drives which tabs and buttons exist — but it is a
 * UI convenience, never a security boundary: the API re-checks every call, so a
 * user who forces a hidden button gets a 403, not access.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, setUnauthenticatedHandler } from '../lib/api';
import { clearTokens, onTokensChanged, readTokens, writeTokens } from '../lib/session-store';
import type { MeResponse } from '../lib/contracts';
import { LoadingState } from '../components/ui';

interface AuthState {
  readonly me: MeResponse | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly signIn: (input: { tenantCode: string; username: string; password: string }) => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly reload: () => Promise<void>;
  readonly can: (permission: string) => boolean;
  /** The branch this session is operating in, resolved from /auth/me. */
  readonly branch: MeResponse['branches'][number] | null;
  readonly primaryRoleName: string;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const loadMe = useCallback(async (): Promise<void> => {
    if (readTokens() === null) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      setMe(await api.me());
      setError(null);
    } catch {
      // Do NOT clear tokens here. `/auth/me` also fails when the phone has no
      // signal, and logging someone out for walking into a cold room would be
      // a bug they cannot recover from without their password.
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // A 401 that survived a refresh means the session is genuinely gone.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setMe(null);
      queryClient.clear();
    });
  }, [queryClient]);

  useEffect(() => {
    void loadMe();
    return onTokensChanged((tokens) => {
      if (tokens === null) setMe(null);
    });
  }, [loadMe]);

  const signIn = useCallback(
    async (input: { tenantCode: string; username: string; password: string }): Promise<void> => {
      const result = await api.login(input);
      writeTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt: result.expiresAt,
      });
      setLoading(true);
      await loadMe();
    },
    [loadMe],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await api.logout();
    } catch {
      // Revoking server-side is best-effort; the local session goes either way.
    }
    clearTokens();
    setMe(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthState>(() => {
    const permissions = new Set(me?.permissions ?? []);
    const activeBranchId = me?.session.activeBranchId ?? null;
    const branch =
      me === null
        ? null
        : (me.branches.find((b) => b.id === activeBranchId) ?? me.branches[0] ?? null);

    return {
      me,
      loading,
      error,
      signIn,
      signOut,
      reload: loadMe,
      can: (permission: string) => permissions.has(permission),
      branch,
      primaryRoleName: me?.roles[0]?.nameVi ?? 'Nhân viên',
    };
  }, [me, loading, error, signIn, signOut, loadMe]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingState label="Đang mở phiên làm việc…" />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === null) throw new Error('useAuth phải được dùng bên trong <AuthProvider>.');
  return context;
}

/**
 * Hide UI the signed-in user has no permission for.
 *
 * Mirrors `PermissionGate` in the shared design system. Hiding is a courtesy,
 * not a control: the API is the authority and re-checks everything.
 */
export function PermissionGate({
  permission,
  children,
  fallback = null,
}: {
  readonly permission: string;
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}): JSX.Element {
  const { can } = useAuth();
  return <>{can(permission) ? children : fallback}</>;
}
